import { Box, Typography, type TypographyProps } from '@mui/material';
import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import logo from '@/assets/logo.png';
import { BRAND_NAME } from '@/constants/brand';
import { gradients } from '@/theme';

/**
 * Motion duration. The previous version ran a 4s cycle whose movement finished at 30% (1.2s) and then
 * held still for 2.8s before repeating — so this preserves the original speed exactly while dropping
 * the dead hold.
 */
const SWEEP_MS = 1200;
const EASING = 'cubic-bezier(0, 0, 0.2, 1)';

export interface BrandMarkProps {
  logoSize?: number;
  variant?: TypographyProps['variant'];
  /** Overrides the rendered HTML tag — e.g. `AuthLayout` needs this as the page's `h1`. */
  component?: TypographyProps['component'];
  /**
   * Render the WHOLE mark — logo and wordmark together — as a navigation link to this path.
   *
   * <p>A real `<a href>` via `RouterLink`, never an `onClick`: the brand-to-home link is one a crawler
   * follows to reach the site root from every public page, and a click handler on a div is invisible to
   * it, unreachable by keyboard and un-openable in a new tab.</p>
   *
   * <p>It is the OUTER element that becomes the anchor rather than a wrapper around it, so the whole
   * logo-plus-wordmark area is the click target and the rendered box model is unchanged — same flex
   * container, same size, same spacing, just an `<a>` instead of a `<div>`. `text-decoration` and
   * `color` are reset because the wordmark paints itself with a gradient and must not gain an
   * underline.</p>
   *
   * <p>Omit it where a link would be pointless or duplicative: the landing page's own hero mark, the
   * footer mark, and the mobile drawer's mark all render unlinked.</p>
   */
  to?: string;
  /**
   * Accessible name for the link — defaults to `"WillnQ home"`.
   *
   * <p>Only meaningful alongside `to`. The anchor would otherwise take its name from its contents
   * (`"WillnQ"`, since the logo is `alt=""` and decorative), which does not say where it goes. The
   * default keeps the visible wordmark as a prefix of the accessible name, which is what WCAG 2.5.3
   * (Label in Name) requires.</p>
   */
  linkLabel?: string;
}

/**
 * The WillnQ logo + wordmark, shared by `TopNav` and `AuthLayout` so the swipe-reveal motion (and any
 * future tweak to it) lives in one place. The logo slides in from the right and uncovers the wordmark.
 *
 * <p>Plays via the Web Animations API (`Element.animate()`), not a CSS `@keyframes`/`animation` pair in
 * `sx`. The previous version used MUI's `sx`-injected `@keyframes`, which never ran on macOS Safari —
 * the logo sat in its resting position from the first frame (no slide at all) and the wordmark, having
 * no clip-path ever applied, just popped fully visible on every remount. WAAPI is imperative: it applies
 * keyframes directly to the element with no dependency on how/when Emotion's runtime stylesheet gets
 * parsed, so it runs identically on Chromium and WebKit.</p>
 *
 * <p>Plays once on mount, then again on hover, by calling `.animate()` again on the SAME nodes — unlike
 * the old `key`-remount trick, nothing ever unmounts, so there is no disappear/reappear flash on replay
 * even when an animation fails to run. Reduced-motion users are unaffected either way — the app's global
 * media query collapses every animation's duration (styles/global.css) — and `Element.animate()` obeys
 * the same `animation-duration` cascade Safari/Chrome resolve for `prefers-reduced-motion`.</p>
 */
export function BrandMark({
  logoSize = 32,
  variant = 'h6',
  component,
  to,
  linkLabel,
}: BrandMarkProps) {
  const logoRef = useRef<HTMLImageElement>(null);
  const revealRef = useRef<HTMLDivElement>(null);

  const play = () => {
    // Not every environment implements Element.animate() (jsdom in tests, some older/embedded
    // WebViews) — skip silently rather than throwing out of a render effect, which would otherwise
    // crash the whole tree via the nearest error boundary.
    if (typeof logoRef.current?.animate !== 'function') return;

    const options: KeyframeAnimationOptions = { duration: SWEEP_MS, easing: EASING, fill: 'both' };
    logoRef.current.animate(
      [{ transform: 'translateX(44px)' }, { transform: 'translateX(0)' }],
      options,
    );
    // Percentage-based inset, not a measured pixel width — needs no layout measurement and clips
    // relative to the wrapper's own box, whatever its rendered size.
    revealRef.current?.animate(
      [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0 0 0)' }],
      options,
    );
  };

  useEffect(play, []);

  const mark = (
    <>
      <Box
        ref={logoRef}
        component="img"
        src={logo}
        alt=""
        width={logoSize}
        height={logoSize}
        sx={{ mr: 0.75, position: 'relative', zIndex: 1 }}
      />
      <Box
        ref={revealRef}
        sx={{ display: 'inline-block', width: 'fit-content', overflow: 'hidden' }}
      >
        <Typography
          variant={variant}
          {...(component ? { component } : {})}
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: gradients.brand,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            width: 'fit-content',
          }}
        >
          {BRAND_NAME}
        </Typography>
      </Box>
    </>
  );

  // The two branches render the SAME contents in the SAME flex box; only the element differs. Written
  // as two returns rather than a spread of conditional props so the anchor's `to` is typed rather than
  // cast, and so it is obvious at a glance that the linked and unlinked marks lay out identically.
  if (to) {
    return (
      <Box
        component={RouterLink}
        to={to}
        aria-label={linkLabel ?? `${BRAND_NAME} home`}
        onMouseEnter={play}
        sx={{
          display: 'flex',
          alignItems: 'center',
          // The wordmark paints itself with a gradient clipped to the glyphs; an inherited underline
          // or link colour would be the only visual change this link makes, so both are reset.
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        {mark}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} onMouseEnter={play}>
      {mark}
    </Box>
  );
}
